import useApi from '../../api/useApi'
import ConfigurationContext from '../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { VideoSession } from '../video/VideoSession'
import { readFile } from './file/readFile'
import { useBaseProcessor } from './useBaseProcessor'
import { useTrackSessionContext } from '../trackSession/TrackSessionContext'
import { TelemetryType, type Device } from '@pitbrain/shared'
import { parseGoProToRawTelemetry } from './gopro/parseGoProTelemetry'
import { rememberRawTelemetry } from './rawTelemetrySource'

export const useGoProProcessor = (
  setProgressPercent: React.Dispatch<React.SetStateAction<number>>
) => {
  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const { setVideoSessions } = useTrackSessionContext()
  const { loadTrack, getFirstCoordinate, processAndCreateSession } =
    useBaseProcessor()
  const { error: apiError } = api

  const processFile = async (file: File, sessionId: string): Promise<void> => {
    // GoPro (LRV) file processing
    const progress = (percent: number) => {
      setProgressPercent(percent)
    }

    const storedTrackSession = await api.trackSessionApi.getTrackSessionByName(
      file.name,
      [TelemetryType.Gps]
    )
    if (storedTrackSession) {
      const sessionFeatures = { hasVideo: true, hasLeanAngle: true }
      await loadTrack(storedTrackSession, sessionFeatures)
      setVideoSessions((prevState) => [
        ...prevState,
        new VideoSession(storedTrackSession.id, file),
      ])
      return
    }

    const cancellationToken = { cancelled: false }
    const results = []
    const result = await readFile(file, { progress, cancellationToken })
    if (result == null) {
      throw new Error(
        'Could not read telemetry data from the file. The file may not contain a GoPro telemetry track.'
      )
    }

    results.push(result)

    // Keep the extracted GPMF track, not the source video: gpmf-extract has
    // already isolated the gpmd stream, which is a few hundred KB per minute
    // against gigabytes for the MP4 it came out of.
    rememberRawTelemetry(sessionId, {
      fileName: `${file.name}.gpmf`,
      contentType: 'application/octet-stream',
      content: new Blob([result.rawData]),
    })

    const rawTelemetry = await parseGoProToRawTelemetry(results)
    const firstCoordinate = getFirstCoordinate(rawTelemetry)
    if (firstCoordinate == null) {
      throw new Error(
        'No GPS data found in the file. Make sure GPS was enabled on the camera and had a satellite lock during the recording.'
      )
    }

    const track = await api.trackApi.findTrack(firstCoordinate)
    const device: Device = { name: 'GoPro', version: '8' }

    await processAndCreateSession(
      rawTelemetry,
      sessionId,
      track,
      { hasLeanAngle: true, hasVideo: true },
      file.name,
      device
    )
    setVideoSessions((prevState) => [
      ...prevState,
      new VideoSession(sessionId, file),
    ])
  }

  return { processFile, error: apiError }
}
